import { Stack } from "@mui/material";
import { ReactComponent as SuccessIcon } from "images/success.svg";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as FP from "utils/fp-js";
import { ActionSelectorForm } from "./ActionSelectorForm";
import { PatientDetailsForm } from "./PatientDetailsForm";
import { QuestionForm } from "./QuestionForm";
import { toastifyConfig, types } from "./ReferralForm.common";

const defaultValues = {
  originatorName: "",
  originatorEmail: "",
  originatorPhone: "",
  recipientName: "",
  buttonType: "",
  patientName: "",
  patientPhone: "",
  patientDob: "",
  type: types.Notification,
  oneTimeCode: "",
  question: "",
  reason: "",
};

export const ReferralForm = ({
  newReferral,
  setNewReferral,
  handleReferralButtonClick,
  originator,
}) => {
  const [prevValues, setPrevValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(types.Notification);
  const [providers, setProviders] = useState([]);

  const { control, setValue, getValues, reset, watch, trigger } = useForm({
    defaultValues,
  });

  const handleResetAndBack = () => {
    setStep(types.Notification);
    reset(prevValues);
  };

  const handleClose = () => {
    setNewReferral(null);
  };

  const showSuccessNotification = () =>
    toast(
      () => (
        <Stack direction={"row"} gap={1}>
          <SuccessIcon />
          <Stack>
            {getValues()?.type} sent.
            <div className="text-sm">
              Thank you for reaching out to {recipientName}. They will contact
              you shortly.
            </div>
          </Stack>
        </Stack>
      ),
      // @ts-ignore
      toastifyConfig
    );

  const onQuestionSubmit = async (newValues) => {
    const values = newValues?.type === "Referral" ? newValues : getValues();

    setLoading(true);
    handleReferralButtonClick(values, showSuccessNotification);
    handleClose();
  };

  useEffect(() => {
    setPrevValues(newReferral);
    reset(newReferral);
    setProviders(newReferral?.providers ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newReferral]);

  const type = watch("type");
  const isButtonEnabled = type !== types.Notification;
  const recipientName = watch("recipientName");
  const { originatorName, originatorEmail, originatorPhone } = getValues();
  const sender = [originatorName, originatorEmail, originatorPhone]
    .filter(Boolean)
    .join(", ");
  const provider = providers?.reduce(
    (acc, { name, speciality }) =>
      [acc, [name, speciality].filter(Boolean).join(", ")]
        .filter(Boolean)
        .join("; "),
    ""
  );

  return FP.match(step)
    .on(
      types.Referral,
      <PatientDetailsForm
        control={control}
        watch={watch}
        getValues={getValues}
        setValue={setValue}
        trigger={trigger}
        reset={reset}
        prevValues={prevValues}
        loading={loading}
        setLoading={setLoading}
        isButtonEnabled={isButtonEnabled}
        onQuestionSubmit={onQuestionSubmit}
        handleResetAndBack={handleResetAndBack}
        recipientName={recipientName}
        provider={provider}
        sender={sender}
        handleClose={handleClose}
        originator={originator}
      />
    )
    .on(
      types.Question,
      <QuestionForm
        handleResetAndBack={handleResetAndBack}
        recipientName={recipientName}
        control={control}
        // watch={watch}
        isButtonEnabled={isButtonEnabled}
        loading={loading}
        onQuestionSubmit={onQuestionSubmit}
        provider={provider}
        sender={sender}
        handleClose={handleClose}
      />
    )
    .otherwise(
      <ActionSelectorForm
        recipientName={recipientName}
        provider={provider}
        sender={sender}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        isButtonEnabled={isButtonEnabled}
        setStep={setStep}
        handleClose={handleClose}
      />
    );
};
